import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';
import { StyleFunction } from 'leaflet';
import { LegendConfig, MapComponent } from '../map/map.component';
import { GenericCardComponent } from '../generic-card/generic-card.component';
import { NgApexchartsModule } from 'ng-apexcharts';
import { CommonModule } from '@angular/common';

export interface GeoMapCardGeometry {
    layerOrder?: number;
    geom: Geometry;
    showTooltip?: boolean;
    [key: string]: any;
}

@UntilDestroy()
@Component({
    selector: 'geo-map-card',
    templateUrl: './geo-map-card.component.html',
    styleUrls: ['./geo-map-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, GenericCardComponent, MapComponent],
})
export class GeoMapCardComponent {
    @Input() title: string = '';
    @Input() mapId: string = '';
    @Input() descriptionTitle?: string;
    @Input() descriptionContent?: string;
    @Input() error: boolean = false;
    @Input() isLoading: boolean = true;
    @Input() cardClass: string = '';
    @Input() set geometries(value: GeoMapCardGeometry[]) {
        this._createGeoJson(value);
    }
    @Input() getStyle: StyleFunction<GeoJsonProperties> = () => ({});
    @Input() getTooltip: (feature: Feature) => string = () => '';
    @Input() legendConfig: LegendConfig[] = [];

    // Bind the cardClass property to the class attribute of the host element
    @HostBinding('class') get hostClasses(): string {
        return this.cardClass;
    }

    public layers: GeoJSON.FeatureCollection[] = [];

    constructor() {}

    private _createGeoJson(geometries: GeoMapCardGeometry[]): void {
        const layerOrderSet = new Set<number>();
        this.layers = [];

        for (const geom of geometries) {
            layerOrderSet.add(geom.layerOrder || 0);
        }
        const layerOrderArray = Array.from(layerOrderSet).sort((a, b) => a - b);
        layerOrderArray.forEach((order) => {
            this._createLayer(geometries, order);
        });
    }

    private _createLayer(geometries: GeoMapCardGeometry[], order: number): void {
        const features = geometries
            .filter((geometry) => !order || geometry.layerOrder === order)
            .map((feature) => {
                const { geometry, ...properties } = feature;

                return {
                    type: 'Feature',
                    properties,
                    geometry,
                } as GeoJSON.Feature;
            });

        this.layers.push({
            type: 'FeatureCollection',
            features,
        });
    }
}
